import { tgpu } from 'typegpu';
import type { TgpuRoot, TgpuBuffer } from 'typegpu';
import type { BaseData } from 'typegpu/data';

export interface Context {
  root: TgpuRoot;
  canvasContext: GPUCanvasContext;
}

export async function createContext(canvas: HTMLCanvasElement): Promise<Context> {
  if (!navigator.gpu) {
    const isSecure = window.isSecureContext;
    const isLocalhost = location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname === '[::1]';
    let msg = 'WebGPU is not available.';
    if (!isSecure) {
      msg += ' WebGPU requires a secure context (HTTPS or localhost).';
      msg += ' Try accessing via http://localhost' + location.pathname;
      msg += ' or use HTTPS.';
    } else {
      msg += ' Check that dom.webgpu.enabled is true in about:config.';
    }
    throw new Error(msg);
  }

  const root = await tgpu.init();
  const canvasContext = root.configureContext({
    canvas,
    alphaMode: 'premultiplied',
  });

  return { root, canvasContext };
}

function getDevice(root: TgpuRoot): GPUDevice {
  return (root as unknown as { device: GPUDevice }).device;
}

export async function readTgpuBufferFloats(root: TgpuRoot, tgpuBuffer: TgpuBuffer<BaseData>, byteLength: number, byteOffset = 0): Promise<Float32Array> {
  const device = getDevice(root);
  const staging = device.createBuffer({
    size: byteLength,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });
  const encoder = device.createCommandEncoder();
  encoder.copyBufferToBuffer(tgpuBuffer.buffer, byteOffset, staging, 0, byteLength);
  device.queue.submit([encoder.finish()]);
  await staging.mapAsync(GPUMapMode.READ);
  const mapped = staging.getMappedRange();
  const result = new Float32Array(mapped.slice(0));
  staging.unmap();
  staging.destroy();
  return result;
}
